import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, ArrowRight, Newspaper } from 'lucide-react'
import { usePublicArticles, type ArticlePreview } from '../hooks/useApi'
import { Card, CardContent } from '../components/ui/Card'
import { SiteFooter } from '../components/SiteFooter'

function formatDate(value: string | null) {
  if (!value) return ''
  return new Date(value).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function ArticleCard({ article }: { article: ArticlePreview }) {
  return (
    <Card className="h-full hover:shadow-lg transition-shadow border border-gray-100">
      <CardContent className="p-6 flex flex-col h-full">
        <div className="flex items-center gap-2 text-sm text-indigo-600 mb-3">
          <Newspaper className="h-4 w-4" />
          <span>Artikel</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{article.title}</h2>
        <p className="text-gray-600 text-sm flex-1 line-clamp-4 mb-4">
          {article.excerpt || 'Baca artikel lengkap untuk tips dan informasi UMKM.'}
        </p>
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {formatDate(article.publishedAt || article.createdAt)}
          </span>
          <Link
            to={`/artikel/${article.slug}`}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
          >
            Baca selengkapnya <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}

export default function Articles() {
  const { data: articles, isLoading } = usePublicArticles(50)

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Artikel & Tips UMKM</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Panduan, tips bisnis, dan informasi untuk mengembangkan UMKM Anda di era digital.
        </p>
      </motion.div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : articles?.length ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article: ArticlePreview) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 py-16">Belum ada artikel. Silakan kembali lagi nanti.</p>
      )}
      </div>

      <SiteFooter />
    </div>
  )
}
